import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../App';
import { dataService } from '../../services/dataService';
import { Student, LearningPath } from '../../data/types';
import PageContainer from '../layouts/PageContainer';

const StudentManagementScreen: React.FC = () => {
  const { activeRole } = useContext(AuthContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [gradeFilter, setGradeFilter] = useState('All Grades');
  const [classFilter, setClassFilter] = useState('All Classes');
  const [students, setStudents] = useState<Student[]>([]);
  const [classes, setClasses] = useState<{id: string, name: string}[]>([]);
  const [learningPaths, setLearningPaths] = useState<LearningPath[]>([]);
  const [loading, setLoading] = useState(true);

  // Reset state when role changes
  useEffect(() => {
    setSearchQuery('');
    setGradeFilter('All Grades');
    setClassFilter('All Classes');
  }, [activeRole]);

  // Fetch data from services
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [studentsData, classesData, learningPathsData] = await Promise.all([
          dataService.getStudents(),
          dataService.getClasses(),
          dataService.getLearningPaths()
        ]);
        
        setStudents(studentsData);
        setClasses(classesData.map(cls => ({
          id: cls.id,
          name: cls.name
        })));
        setLearningPaths(learningPathsData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Get unique grades from students
  const grades = Array.from(new Set(students.map(student => student.grade)))
    .sort((a, b) => {
      // Sort grades with K first, then numeric order
      if (a === 'K') return -1;
      if (b === 'K') return 1;
      return parseInt(a) - parseInt(b);
    });
  
  // Filter students based on search and filters
  const filteredStudents = students.filter(student => {
    const matchesSearch = 
      searchQuery === '' ||
      student.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.id.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesGrade = 
      gradeFilter === 'All Grades' || 
      student.grade === gradeFilter;
    
    const matchesClass = 
      classFilter === 'All Classes' || 
      student.classes.some(cls => {
        const classObj = classes.find(c => c.id === cls);
        return classObj && classObj.id === classFilter;
      });
    
    return matchesSearch && matchesGrade && matchesClass;
  });

  // Calculate overall progress for each student (average of all standards)
  const calculateProgress = (student: Student) => {
    const progressValues = Object.values(student.progress);
    if (progressValues.length === 0) return 0;
    
    const completedCount = progressValues.filter(p => 
      p.learningPhaseStatus === 'Completed'
    ).length;
    
    const practiceCompletedCount = progressValues.filter(p => 
      p.learningPhaseStatus === 'PracticeCompleted'
    ).length;
    
    const videoCompletedCount = progressValues.filter(p => 
      p.learningPhaseStatus === 'VideoCompleted'
    ).length;
    
    // Weight completed standards higher
    const weightedScore = (completedCount * 100 + practiceCompletedCount * 66 + videoCompletedCount * 33) / progressValues.length;
    return Math.min(Math.round(weightedScore), 100);
  };

  // Get current learning path for a student
  const getCurrentLearningPath = (student: Student) => {
    // If student has no classes or no progress, return 'Not Assigned'
    if (student.classes.length === 0 || Object.keys(student.progress).length === 0) {
      return 'Not Assigned';
    }
    
    // Find the class and its learning paths
    const studentClass = classes.find(c => c.id === student.classes[0]);
    if (!studentClass) return 'Not Assigned';
    
    // First try to find a learning path assigned directly to the student
    const studentLearningPath = learningPaths.find(path => 
      path.assignedTo.some(assignment => 
        assignment.assignedToType === 'Student' && 
        assignment.assignedToId === student.id
      )
    );
    
    if (studentLearningPath) {
      return studentLearningPath.name;
    }
    
    // If no direct assignment, look for class assignment
    const classLearningPath = learningPaths.find(path => 
      path.assignedTo.some(assignment => 
        assignment.assignedToType === 'Class' && 
        assignment.assignedToId === student.classes[0]
      )
    );
    
    return classLearningPath ? classLearningPath.name : 'Not Assigned';
  };

  return (
    <PageContainer title="My Students" userRole="Teacher">
      <div className="bg-white rounded-lg shadow p-6">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div>
            <div className="flex flex-wrap items-center justify-between mb-6">
              <div className="w-full md:w-auto mb-4 md:mb-0">
                <label htmlFor="search-students" className="block text-sm font-medium text-gray-700 mb-1">
                  Search Students
                </label>
                <div className="relative w-full md:w-80">
                  <input
                    id="search-students"
                    type="text"
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    placeholder="Search by name, ID, or grade"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                    <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                <div className="w-full sm:w-auto">
                  <label htmlFor="filter-grade" className="block text-sm font-medium text-gray-700 mb-1">Grade Level</label>
                  <select 
                    id="filter-grade"
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary focus:border-primary"
                    value={gradeFilter}
                    onChange={(e) => setGradeFilter(e.target.value)}
                  >
                    <option>All Grades</option>
                    {grades.map(grade => (
                      <option key={grade}>Grade {grade}</option>
                    ))}
                  </select>
                </div>
                <div className="w-full sm:w-auto">
                  <label htmlFor="filter-class" className="block text-sm font-medium text-gray-700 mb-1">Class</label>
                  <select 
                    id="filter-class"
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary focus:border-primary"
                    value={classFilter}
                    onChange={(e) => setClassFilter(e.target.value)}
                  >
                    <option>All Classes</option>
                    {classes.map(cls => (
                      <option key={cls.id} value={cls.id}>{cls.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Student
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Grade
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Class
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Current Learning Path
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Progress
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredStudents.length > 0 ? (
                    filteredStudents.map(student => {
                      const progress = calculateProgress(student);
                      const studentClass = student.classes.length > 0 
                        ? classes.find(c => c.id === student.classes[0])
                        : null;
                      const learningPath = getCurrentLearningPath(student);

                      return (
                        <tr key={student.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                {student.avatar ? (
                                  <img 
                                    className="h-10 w-10 rounded-full" 
                                    src={student.avatar} 
                                    alt={`${student.firstName} ${student.lastName}`} 
                                  />
                                ) : (
                                  <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                    <span className="text-sm font-medium text-gray-600">
                                      {student.firstName.charAt(0)}{student.lastName.charAt(0)}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {student.firstName} {student.lastName}
                                </div>
                                <div className="text-sm text-gray-500">{student.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{student.id}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">Grade {student.grade}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {studentClass ? studentClass.name : 'Not Assigned'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {learningPath}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                              <div 
                                className="bg-primary h-2.5 rounded-full" 
                                style={{ width: `${progress}%` }}
                              ></div>
                            </div>
                            <div className="text-xs mt-1 text-gray-500">{progress}% Complete</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button className="text-primary hover:text-primary-700">View Details</button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                        No students found matching your criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default StudentManagementScreen;
